/**
 * ALL IS WELL PHARMACY — Data & Request Utilities
 *
 * Phase 1: PATIENT → PHARMACY → REQUEST → WHATSAPP → PHARMACY TEAM
 * Future:  PATIENT → PHARMACY → SECURE BACKEND → ERP → STOCK CHECK → ORDER → PAYMENT → DELIVERY
 *
 * IMPORTANT: This is a prototype lead/request system.
 * Do NOT claim medicine availability. Do NOT confirm orders automatically.
 * Do NOT invent medicine prices.
 */

import { hospitalConfig } from '@/data/hospitalConfig';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface PharmacyMedicineRequest {
  requestId: string;
  patientName: string;
  patientMobile: string;
  centreName: string;
  medicineRequest: string;
  hasPrescription: boolean;
  createdAt: string;
}

export interface PharmacyRefillRequest {
  requestId: string;
  patientName: string;
  patientMobile: string;
  centreName: string;
  prescriptionId: string;
  prescriptionLabel: string;
  doctor: string;
  createdAt: string;
}

// ─── Demo Prescriptions (clearly labelled — never real data) ─────────────────

export interface DemoPharmacyPrescription {
  id: string;
  label: string;
  doctor: string;
  department: string;
  centre: string;
  date: string;
  medicineCount: number;
  status: 'active' | 'completed';
  isDemo: true;
}

export const DEMO_PHARMACY_PRESCRIPTIONS: DemoPharmacyPrescription[] = [
  {
    id: 'rx-demo-001',
    label: 'Prescription — Aug 2026',
    doctor: 'Dr. [Verified Doctor]',
    department: 'General Medicine',
    centre: 'All Is Well Super Clinic Burhanpur',
    date: '10 Aug 2026',
    medicineCount: 5,
    status: 'active',
    isDemo: true,
  },
  {
    id: 'rx-demo-002',
    label: 'Prescription — Jul 2026',
    doctor: 'Dr. [Verified Doctor]',
    department: 'Cardiology',
    centre: 'All Is Well Super Clinic Burhanpur',
    date: '15 Jul 2026',
    medicineCount: 3,
    status: 'active',
    isDemo: true,
  },
  {
    id: 'rx-demo-003',
    label: 'Prescription — Jun 2026',
    doctor: 'Dr. [Verified Doctor]',
    department: 'Orthopaedics',
    centre: 'All Is Well Super Clinic, Khandwa',
    date: '02 Jun 2026',
    medicineCount: 4,
    status: 'completed',
    isDemo: true,
  },
];

// ─── Request ID Generator ─────────────────────────────────────────────────────

/**
 * Generates a unique pharmacy request ID: AIW-PHARM-DDMMYY-XXXXX
 * Example: AIW-PHARM-110826-00312
 */
export function generatePharmacyRequestId(): string {
  const now = new Date();
  const dd = String(now.getDate()).padStart(2, '0');
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const yy = String(now.getFullYear()).slice(-2);
  const seq = String(Math.floor(Math.random() * 99999) + 1).padStart(5, '0');
  return `AIW-PHARM-${dd}${mm}${yy}-${seq}`;
}

// ─── WhatsApp Message Builders ────────────────────────────────────────────────

export function buildMedicineRequestWhatsAppMessage(req: PharmacyMedicineRequest): string {
  return `Hello All Is Well Pharmacy Team,

I would like assistance with medicines.

NEW PHARMACY REQUEST
━━━━━━━━━━━━━━━━

REQUEST ID:
${req.requestId}

PATIENT
Name: ${req.patientName}
Mobile: ${req.patientMobile}

CENTRE
${req.centreName}

MEDICINE REQUEST
${req.medicineRequest}

${req.hasPrescription ? 'Note: Prescription will be shared separately.' : ''}

Please check availability and assist me.

━━━━━━━━━━━━━━━━
All Is Well Experience™`;
}

export function buildRefillRequestWhatsAppMessage(req: PharmacyRefillRequest): string {
  return `Hello All Is Well Pharmacy Team,

I would like to request a medicine refill.

NEW REFILL REQUEST
━━━━━━━━━━━━━━━━

REQUEST ID:
${req.requestId}

PATIENT
Name: ${req.patientName}
Mobile: ${req.patientMobile}

CENTRE
${req.centreName}

PRESCRIPTION
${req.prescriptionLabel}
Doctor: ${req.doctor}

Please check availability and process the refill.

━━━━━━━━━━━━━━━━
All Is Well Experience™`;
}

export function openPharmacyWhatsApp(message: string): void {
  const url = `${hospitalConfig.officialWhatsAppLink}?text=${encodeURIComponent(message)}`;
  window.open(url, '_blank', 'noopener,noreferrer');
}
